import { config } from '../config'
import type { Survey, SurveyListItem, SurveyResponse, ListResponse } from '../types/survey'

const BASE = config.apiBaseUrl

export class ApiResponseError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${BASE}${path}`
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  })

  if (!response.ok) {
    let errorBody: any = null
    try {
      errorBody = await response.json()
    } catch {
      // ignore parse error
    }
    throw new ApiResponseError(response.status, errorBody?.message ?? errorBody?.error ?? `HTTP ${response.status}: ${response.statusText}`)
  }

  if (response.status === 204) return undefined as unknown as T
  return response.json() as Promise<T>
}

export const surveyApi = {
  listSurveys: async (page = 1, pageSize = 20): Promise<ListResponse<SurveyListItem>> => {
    const data = await request<SurveyListItem[] | ListResponse<SurveyListItem>>(`/v1/surveys?page=${page}&pageSize=${pageSize}`)
    if (Array.isArray(data)) return { items: data, total: data.length, page, pageSize }
    return data
  },

  getSurvey: (surveyId: string): Promise<Survey> => request<Survey>(`/v1/surveys/${encodeURIComponent(surveyId)}`),

  registerRespondent: (
    surveyId: string,
    payload: { firstName: string; lastName: string; email?: string; phone?: string },
  ): Promise<{ respondentId: string; token: string }> =>
    request(`/v1/surveys/${encodeURIComponent(surveyId)}/respondents`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  verifyRespondentToken: (
    token: string,
  ): Promise<{ respondentId: string; token: string; surveyId: string; firstName: string; lastName: string; email?: string; phone?: string }> =>
    request(`/v1/respondents/verify/${encodeURIComponent(token)}`),

  submitResponse: (
    surveyId: string,
    respondentId: string,
    data: Record<string, unknown>,
    meta?: { startedAt?: string; durationMs?: number },
  ): Promise<{ responseId: string }> => {
    const payload: Omit<SurveyResponse, 'surveyId'> & { surveyId: string } = {
      surveyId,
      respondentId,
      data,
      submittedAt: new Date().toISOString(),
      startedAt: meta?.startedAt,
      durationMs: meta?.durationMs,
      metadata: {
        userAgent: navigator.userAgent,
        locale: navigator.language,
      },
    }
    return request<{ responseId: string }>(`/v1/surveys/${encodeURIComponent(surveyId)}/responses`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
}
